/**
 * 全屏加载遮罩：统一使用进度条展示，进度 0–100 来自 renderer 的 progress 事件。
 * loadProgress 在会话开始即置 0，无独立 spinner/不确定态，避免「先转圈再切进度条」的割裂。
 */
import { useEffect, useRef, useState } from 'react';
import { useViewerStore } from '@stores/viewerStore';

const PROGRESS_ANIMATION_MS = 500;

function clampProgress(value: number): number {
  return Math.min(Math.max(value, 0), 100);
}

export function LoadingOverlay() {
  const loadProgress = useViewerStore((s) => s.loadProgress);
  const target = clampProgress(loadProgress ?? 0);

  // 缓存命中或缺少 Content-Length 时，真实进度可能直接跳到 100%。
  // 数字与进度条共用同一显示值，避免 CSS 二次过渡造成两者不同步。
  const [display, setDisplay] = useState(0);
  const displayRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = displayRef.current;
    const startTime = performance.now();

    const tick = (now: number) => {
      const t = Math.min((now - startTime) / PROGRESS_ANIMATION_MS, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = start + (target - start) * eased;
      displayRef.current = next;
      setDisplay(next);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      displayRef.current = target;
      setDisplay(target);
      rafRef.current = null;
    };

    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [target]);

  const progress = clampProgress(display);
  const progressText = Math.round(progress);

  return (
    <div className="pointer-events-auto absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mb-4 flex items-center">
        <div className="w-64">
          <div className="overflow-hidden rounded-full bg-white/20">
            <div className="h-1.5 rounded-full bg-white" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <span className="ml-2 font-mono text-xs text-white/70">{progressText}%</span>
      </div>

      <p className="text-sm text-white/60">Loading scene…</p>
    </div>
  );
}
